(() => {
  const NAV_SELECTOR = '[data-hindi-collection-nav]';
  const LINK_SELECTOR = '[data-hindi-collection-link]';
  const TRACK_SELECTOR = '.hindi-collection-nav__track';
  const GRID_SELECTOR = '#ProductGridContainer';
  const FILTERS_SELECTOR = '#main-collection-filters';
  const COMPONENT_SELECTOR = 'collection-component';
  const HERO_TITLE_SELECTOR = '.hindi-collection-hero__title';
  const HERO_DESCRIPTION_SELECTOR = '.hindi-collection-hero__description';
  const HERO_COUNT_SELECTOR = '.hindi-collection-hero__count';
  const HERO_IMAGE_SELECTOR = '.hindi-collection-hero__image-container';
  const cache = new Map();
  let activeController = null;
  let requestSequence = 0;

  const normalisePath = (value) => {
    try {
      const url = new URL(value, window.location.origin);
      return url.pathname.replace(/\/$/, '') || '/';
    } catch (_) {
      return String(value || '');
    }
  };

  const cleanVisibleText = (value = '') =>
    String(value)
      .replace(/[\u200B-\u200D\u2060\uFEFF]/g, '')
      .replace(/\u00A0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const titleFromHandle = (link) => {
    const handle = cleanVisibleText(link?.dataset?.collectionHandle || '');
    if (!handle || handle === 'all') return '';
    return handle
      .split('-')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  const getLinkLabel = (link) =>
    cleanVisibleText(link?.querySelector('.hindi-collection-nav__label')?.textContent) ||
    cleanVisibleText(link?.dataset?.fallbackTitle) ||
    cleanVisibleText(link?.getAttribute('aria-label')) ||
    titleFromHandle(link);

  const ensureVisibleLabels = (nav) => {
    nav.querySelectorAll(LINK_SELECTOR).forEach((link) => {
      const label = link.querySelector('.hindi-collection-nav__label');
      if (!label) return;

      const currentText = cleanVisibleText(label.textContent);
      const fallback = getLinkLabel(link);

      if (!currentText && fallback) label.textContent = fallback;
      if (fallback && !cleanVisibleText(link.getAttribute('aria-label'))) {
        link.setAttribute('aria-label', fallback);
      }
    });
  };

  const canIntercept = (event, link) => {
    if (event.defaultPrevented || event.button !== 0) return false;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (link.target && link.target !== '_self') return false;

    const url = new URL(link.href, window.location.href);
    return url.origin === window.location.origin;
  };

  const getGridSectionId = () => document.querySelector('#product-grid')?.dataset.id || '';
  const getLinks = (nav) => [...nav.querySelectorAll(LINK_SELECTOR)];

  const getVisibilityDelta = (track, link, tolerance = 10) => {
    if (!track || !link) return 0;
    const trackRect = track.getBoundingClientRect();
    const linkRect = link.getBoundingClientRect();
    const leftLimit = trackRect.left + tolerance;
    const rightLimit = trackRect.right - tolerance;

    if (linkRect.left < leftLimit) return linkRect.left - leftLimit;
    if (linkRect.right > rightLimit) return linkRect.right - rightLimit;
    return 0;
  };

  const linkIsFullyVisible = (track, link, tolerance = 10) => Math.abs(getVisibilityDelta(track, link, tolerance)) < 1;

  // Scroll only the category track. Unlike scrollIntoView, this cannot move the page vertically.
  const ensureLinkVisible = (nav, link, behavior = 'smooth') => {
    const track = nav.querySelector(TRACK_SELECTOR);
    if (!track || !link) return;
    const delta = getVisibilityDelta(track, link);
    if (Math.abs(delta) < 1) return;
    track.scrollBy({ left: delta, top: 0, behavior });
  };

  const clearSelectionState = (nav) => {
    getLinks(nav).forEach((link) => {
      link.classList.remove('is-active', 'is-pending');
      link.removeAttribute('aria-current');
    });
  };

  // The clicked item becomes the active item immediately and keeps that visual state
  // while its small loader indicates that the products are still updating.
  const setPendingLink = (nav, link) => {
    clearSelectionState(nav);
    if (!link) return;
    link.classList.add('is-active', 'is-pending');
    link.setAttribute('aria-current', 'page');
    ensureLinkVisible(nav, link, 'smooth');
  };

  const updateActiveLink = (nav, targetUrl, { reveal = false, behavior = 'auto' } = {}) => {
    ensureVisibleLabels(nav);
    const targetPath = normalisePath(targetUrl);
    let activeLink = null;

    getLinks(nav).forEach((link) => {
      const isActive = normalisePath(link.href) === targetPath;
      link.classList.toggle('is-active', isActive);
      link.classList.remove('is-pending');
      if (isActive) {
        link.setAttribute('aria-current', 'page');
        activeLink = link;
      } else {
        link.removeAttribute('aria-current');
      }
    });

    if (reveal && activeLink) ensureLinkVisible(nav, activeLink, behavior);
    return activeLink;
  };

  const setLoading = (nav, loading) => {
    nav.classList.toggle('is-loading', loading);
    nav.setAttribute('aria-busy', loading ? 'true' : 'false');

    getLinks(nav).forEach((link) => {
      if (loading) link.setAttribute('aria-disabled', 'true');
      else link.removeAttribute('aria-disabled');
    });

    const grid = document.querySelector(GRID_SELECTOR);
    grid?.classList.toggle('hindi-collection-switching', loading);
    grid?.setAttribute('aria-busy', loading ? 'true' : 'false');

    const status = nav.querySelector('[data-hindi-collection-status]');
    if (status) status.textContent = loading ? nav.dataset.loadingText || '' : nav.dataset.loadedText || '';
  };

  const buildSectionsUrl = (targetUrl, nav) => {
    const bannerSectionId = nav.dataset.bannerSectionId || '';
    const gridSectionId = getGridSectionId();
    if (!bannerSectionId || !gridSectionId) throw new Error('Collection section IDs were not found.');

    const url = new URL(targetUrl, window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.set('sections', `${bannerSectionId},${gridSectionId}`);
    return { url, bannerSectionId, gridSectionId };
  };

  const fetchSections = async (targetUrl, nav, signal) => {
    const { url, bannerSectionId, gridSectionId } = buildSectionsUrl(targetUrl, nav);
    const cacheKey = url.toString();
    if (cache.has(cacheKey)) return cache.get(cacheKey);

    const response = await fetch(cacheKey, {
      signal,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
      },
    });

    if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);
    const payload = await response.json();
    const result = {
      bannerHtml: payload[bannerSectionId],
      gridHtml: payload[gridSectionId],
    };

    if (!result.gridHtml) throw new Error('The product-grid section was not returned.');
    cache.set(cacheKey, result);
    return result;
  };

  const parseSection = (html) => new DOMParser().parseFromString(html || '', 'text/html');

  const updateHero = (bannerDocument) => {
    if (!bannerDocument) return;

    const nextTitle = bannerDocument.querySelector(HERO_TITLE_SELECTOR);
    const currentTitle = document.querySelector(HERO_TITLE_SELECTOR);
    if (nextTitle && currentTitle) currentTitle.innerHTML = nextTitle.innerHTML;

    const nextCount = bannerDocument.querySelector(HERO_COUNT_SELECTOR);
    const currentCount = document.querySelector(HERO_COUNT_SELECTOR);
    if (nextCount && currentCount) currentCount.replaceWith(document.importNode(nextCount, true));
    else if (currentCount && !nextCount) currentCount.remove();

    const nextDescription = bannerDocument.querySelector(HERO_DESCRIPTION_SELECTOR);
    const currentDescription = document.querySelector(HERO_DESCRIPTION_SELECTOR);
    const currentTextWrapper = document.querySelector('.hindi-collection-hero__text-wrapper');
    const currentCountAfterUpdate = document.querySelector(HERO_COUNT_SELECTOR);

    if (currentDescription && nextDescription) {
      currentDescription.replaceWith(document.importNode(nextDescription, true));
    } else if (currentDescription && !nextDescription) {
      currentDescription.remove();
    } else if (!currentDescription && nextDescription && currentTextWrapper) {
      const importedDescription = document.importNode(nextDescription, true);
      if (currentCountAfterUpdate) currentTextWrapper.insertBefore(importedDescription, currentCountAfterUpdate);
      else currentTextWrapper.appendChild(importedDescription);
    }

    const nextHero = bannerDocument.querySelector('.hindi-collection-hero');
    const currentHero = document.querySelector('.hindi-collection-hero');
    if (nextHero && currentHero) {
      const nextHasImage = nextHero.classList.contains('hindi-collection-hero--with-image');
      currentHero.classList.toggle('hindi-collection-hero--with-image', nextHasImage);
      currentHero.classList.toggle('hindi-collection-hero--text-only', !nextHasImage);
    }

    const nextImage = bannerDocument.querySelector(HERO_IMAGE_SELECTOR);
    const currentImage = document.querySelector(HERO_IMAGE_SELECTOR);
    const currentInner = document.querySelector('.hindi-collection-hero__inner');
    if (currentImage && nextImage) currentImage.replaceWith(document.importNode(nextImage, true));
    else if (currentImage && !nextImage) currentImage.remove();
    else if (!currentImage && nextImage && currentInner) currentInner.appendChild(document.importNode(nextImage, true));
  };

  const useNavigationLabelForAllMenu = (link) => {
    if (!link || link.dataset.allMenuItem !== 'true') return;
    const title = document.querySelector(HERO_TITLE_SELECTOR);
    const label = getLinkLabel(link);
    if (!title || !label) return;

    const accessiblePrefix = title.querySelector('.visually-hidden')?.cloneNode(true);
    title.replaceChildren();
    if (accessiblePrefix) title.appendChild(accessiblePrefix);
    title.appendChild(document.createTextNode(label));
  };

  const updateProductArea = (gridDocument) => {
    const nextGrid = gridDocument.querySelector(GRID_SELECTOR);
    const currentGrid = document.querySelector(GRID_SELECTOR);
    if (!nextGrid || !currentGrid) throw new Error('The updated product grid could not be found.');

    const nextFilters = gridDocument.querySelector(FILTERS_SELECTOR);
    const currentFilters = document.querySelector(FILTERS_SELECTOR);
    const nextComponent = gridDocument.querySelector(COMPONENT_SELECTOR);
    const currentComponent = document.querySelector(COMPONENT_SELECTOR);

    currentGrid.replaceWith(document.importNode(nextGrid, true));

    if (currentFilters && nextFilters) currentFilters.replaceWith(document.importNode(nextFilters, true));
    else if (currentFilters && !nextFilters) currentFilters.remove();
    else if (!currentFilters && nextFilters) {
      const newGrid = document.querySelector(GRID_SELECTOR);
      newGrid?.parentElement?.insertBefore(document.importNode(nextFilters, true), newGrid);
    }

    if (currentComponent && nextComponent) {
      ['data-collection-id', 'view-event-payload'].forEach((attribute) => {
        const value = nextComponent.getAttribute(attribute);
        if (value !== null) currentComponent.setAttribute(attribute, value);
      });
    }
  };

  const reinitialiseUpdatedContent = () => {
    if (typeof window.initializeScrollAnimationTrigger === 'function') window.initializeScrollAnimationTrigger();
    document.dispatchEvent(new CustomEvent('hindi:collection:updated'));
  };

  const renderCollection = ({ bannerHtml, gridHtml }, selectedLink) => {
    const bannerDocument = bannerHtml ? parseSection(bannerHtml) : null;
    const gridDocument = parseSection(gridHtml);
    updateProductArea(gridDocument);
    updateHero(bannerDocument);
    useNavigationLabelForAllMenu(selectedLink);
    reinitialiseUpdatedContent();
  };

  const preserveViewport = (nav, callback) => {
    const beforeTop = nav.getBoundingClientRect().top;
    callback();
    const afterTop = nav.getBoundingClientRect().top;
    const shift = afterTop - beforeTop;
    if (Math.abs(shift) > 1) window.scrollBy({ top: shift, left: 0, behavior: 'auto' });
  };

  const loadCollection = async (nav, targetUrl, { pushHistory = true, link = null } = {}) => {
    if (nav.dataset.ajaxNavigation !== 'true') {
      window.location.href = targetUrl;
      return;
    }

    const targetPath = normalisePath(targetUrl);
    if (targetPath === normalisePath(window.location.href) && pushHistory) {
      updateActiveLink(nav, targetUrl, { reveal: true, behavior: 'smooth' });
      useNavigationLabelForAllMenu(link);
      return;
    }

    activeController?.abort();
    activeController = new AbortController();
    const requestId = ++requestSequence;

    setPendingLink(nav, link);
    setLoading(nav, true);

    try {
      const sections = await fetchSections(targetUrl, nav, activeController.signal);
      if (requestId !== requestSequence) return;

      preserveViewport(nav, () => renderCollection(sections, link));

      const target = new URL(targetUrl, window.location.href);
      if (pushHistory) {
        window.history.pushState(
          { hindiCollectionNavigation: true },
          '',
          `${target.pathname}${target.search}${target.hash}`,
        );
      }

      // No second reveal here: the chip was already revealed once at click time.
      updateActiveLink(nav, target.toString(), { reveal: false });
      useNavigationLabelForAllMenu(link);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('[Hindi quick menu]', error);
      window.location.assign(targetUrl);
    } finally {
      if (requestId === requestSequence) setLoading(nav, false);
    }
  };

  const enableDesktopDragScroll = (nav) => {
    const track = nav.querySelector(TRACK_SELECTOR);
    if (!track || track.dataset.hindiDragScrollReady === 'true') return;
    track.dataset.hindiDragScrollReady = 'true';

    let pointerId = null;
    let startX = 0;
    let startY = 0;
    let startScrollLeft = 0;
    let dragged = false;
    let suppressNextClick = false;

    const reset = () => {
      pointerId = null;
      dragged = false;
      track.classList.remove('is-dragging');
    };

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (track.scrollWidth <= track.clientWidth + 2) return;

      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      startScrollLeft = track.scrollLeft;
      dragged = false;
    });

    track.addEventListener('pointermove', (event) => {
      if (pointerId !== event.pointerId) return;
      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;

      if (!dragged) {
        if (Math.abs(deltaX) < 8) return;
        if (Math.abs(deltaY) > Math.abs(deltaX)) {
          reset();
          return;
        }
        dragged = true;
        track.classList.add('is-dragging');
      }

      track.scrollLeft = startScrollLeft - deltaX;
      event.preventDefault();
    });

    const finish = () => {
      if (pointerId === null) return;
      const didDrag = dragged;
      reset();
      if (didDrag) {
        suppressNextClick = true;
        window.setTimeout(() => {
          suppressNextClick = false;
        }, 0);
      }
    };

    track.addEventListener('pointerup', finish);
    track.addEventListener('pointercancel', finish);
    track.addEventListener('pointerleave', () => {
      if (pointerId !== null && !dragged) reset();
    });
    track.addEventListener('dragstart', (event) => event.preventDefault());

    track.addEventListener(
      'click',
      (event) => {
        if (!suppressNextClick) return;
        suppressNextClick = false;
        event.preventDefault();
        event.stopPropagation();
      },
      true,
    );
  };

  const enableArrowControls = (nav) => {
    const track = nav.querySelector(TRACK_SELECTOR);
    const leftButton = nav.querySelector('[data-hindi-collection-scroll="left"]');
    const rightButton = nav.querySelector('[data-hindi-collection-scroll="right"]');
    if (!track || !leftButton || !rightButton || nav.dataset.hindiArrowControlsReady === 'true') return;
    nav.dataset.hindiArrowControlsReady = 'true';

    const getState = () => {
      const trackRect = track.getBoundingClientRect();
      const links = getLinks(nav);
      const tolerance = 7;
      const hiddenLeft = links.filter((link) => link.getBoundingClientRect().left < trackRect.left - tolerance);
      const hiddenRight = links.filter((link) => link.getBoundingClientRect().right > trackRect.right + tolerance);
      return {
        hasOverflow: track.scrollWidth > track.clientWidth + 2,
        hiddenLeft,
        hiddenRight,
      };
    };

    const setArrowState = (button, enabled) => {
      button.disabled = !enabled;
      button.setAttribute('aria-hidden', enabled ? 'false' : 'true');
      button.tabIndex = enabled ? 0 : -1;
    };

    const updateButtons = () => {
      const { hasOverflow, hiddenLeft, hiddenRight } = getState();
      nav.classList.toggle('has-scroll-overflow', hasOverflow);
      setArrowState(leftButton, hasOverflow && hiddenLeft.length > 0);
      setArrowState(rightButton, hasOverflow && hiddenRight.length > 0);
    };

    const scrollToNextHidden = (direction) => {
      const { hiddenLeft, hiddenRight } = getState();
      const target = direction === 'left' ? hiddenLeft.at(-1) : hiddenRight[0];
      if (!target) return;
      ensureLinkVisible(nav, target, 'smooth');
      window.setTimeout(updateButtons, 350);
    };

    leftButton.addEventListener('click', () => scrollToNextHidden('left'));
    rightButton.addEventListener('click', () => scrollToNextHidden('right'));
    track.addEventListener('scroll', () => window.requestAnimationFrame(updateButtons), { passive: true });
    window.addEventListener('resize', () => window.requestAnimationFrame(updateButtons));

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(() => window.requestAnimationFrame(updateButtons));
      resizeObserver.observe(track);
    }

    updateButtons();
  };

  const initialiseNav = (nav) => {
    if (!nav || nav.dataset.hindiCollectionNavReady === 'true') return;
    nav.dataset.hindiCollectionNavReady = 'true';

    ensureVisibleLabels(nav);
    enableDesktopDragScroll(nav);
    enableArrowControls(nav);
    const initialActive = updateActiveLink(nav, window.location.href);
    if (initialActive) ensureLinkVisible(nav, initialActive, 'auto');
    useNavigationLabelForAllMenu(initialActive);

    nav.addEventListener('click', (event) => {
      const link = event.target.closest(LINK_SELECTOR);
      if (!link || !nav.contains(link) || !canIntercept(event, link)) return;
      event.preventDefault();
      if (nav.classList.contains('is-loading')) return;
      loadCollection(nav, link.href, { pushHistory: true, link });
    });
  };

  const initialiseAll = () => document.querySelectorAll(NAV_SELECTOR).forEach(initialiseNav);

  window.addEventListener('popstate', () => {
    const nav = document.querySelector(NAV_SELECTOR);
    if (!nav) return;
    const matchingLink = getLinks(nav).find(
      (link) => normalisePath(link.href) === normalisePath(window.location.href),
    );
    loadCollection(nav, window.location.href, { pushHistory: false, link: matchingLink || null });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialiseAll, { once: true });
  } else {
    initialiseAll();
  }

  document.addEventListener('shopify:section:load', initialiseAll);
})();
